'use client';

import { useForm } from '@tanstack/react-form';
import { Container } from '@veranda/shared/components/container';
import { ModuleWrapper } from '@veranda/shared/components/module-wrapper';
import type { HomePageProps } from '@veranda/shared/components/pages/home/client';
import { subscribeToNewsletter } from '@veranda/shared/klaviyo/actions';
import {
  type NewsletterFormSchema,
  newsletterFormSchema,
} from '@veranda/shared/klaviyo/schema';
import { useAction } from 'next-safe-action/hooks';
import { toast } from 'sonner';

type Props = Extract<
  NonNullable<HomePageProps['modules']>[number],
  { moduleType: 'newsletterSignupModule' }
>;

export function NewsletterSignupModule({ data }: { data: Props }) {
  const form = useForm({
    defaultValues: {
      email: '',
      klaviyoListId: undefined,
    } as NewsletterFormSchema,
    validators: {
      onBlur: newsletterFormSchema,
    },
    onSubmit: ({ value }) => {
      execute(value);
    },
  });

  const { execute, isExecuting } = useAction(subscribeToNewsletter, {
    onSuccess({ data }) {
      form.reset();
      if (data?.success) {
        toast.success(data?.message);
      } else {
        toast.error(data?.message);
      }
    },
    onError(error) {
      if (error.error.serverError) {
        toast.error(error.error.serverError);
      }
      if (error.error.validationErrors) {
        toast.error(error.error.validationErrors._errors);
      }
    },
  });

  if (!data.content) return null;

  const content =
    'content' in data.content ? data.content.content : data.content;

  if (!content) return null;

  return (
    <ModuleWrapper
      topPadding={content.moduleSettings?.topPadding}
      bottomPadding={content.moduleSettings?.bottomPadding}
      colorTheme={content.colorTheme}
    >
      <Container className="flex h-full flex-col items-center justify-center text-center">
        <div className="mb-3 w-full max-w-4xl">
          {content.title && (
            <h2 className="typography-clash-h2 font-medium text-[--text-color]">
              {content.title}
            </h2>
          )}
        </div>
        <div className="mb-6 w-full max-w-3xl">
          {content.description && (
            <p className="font-clash font-medium text-[--text-color]">
              {content.description}
            </p>
          )}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="flex w-full max-w-lg flex-col gap-2 sm:flex-row"
        >
          <form.Field
            name="email"
            children={(field) => (
              <div className="relative w-full">
                <label htmlFor={field.name} className="sr-only">
                  Email
                </label>
                <input
                  type="email"
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Email"
                  className="w-full flex-1 border border-[--border-color] bg-transparent px-4 py-3 text-[--text-color] placeholder:text-[--text-color] placeholder:text-[--text-color]/60 focus:outline-none"
                />
                {field.state.meta.isTouched &&
                field.state.meta.errors.length ? (
                  <span className="-bottom-6 absolute left-0 text-[--text-color] text-sm">
                    {field.state.meta.errors
                      .map((error) => error?.message)
                      .join(', ')}
                  </span>
                ) : null}
              </div>
            )}
          />
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <button
                type="submit"
                disabled={!canSubmit || isSubmitting || isExecuting}
                className="border border-[--border-color] bg-transparent px-8 py-2 font-clash font-medium text-[--text-color] hover:bg-[--text-color] hover:text-[--background-color]"
              >
                {isExecuting || isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            )}
          />
        </form>
      </Container>
    </ModuleWrapper>
  );
}
